import { API_CONFIG } from '@/config/api'
import { baseService } from './baseService'

/** The 7 canonical emotion keys, matching the live-demo EmotionKey set. */
export type EmotionKey = 'happy' | 'surprised' | 'neutral' | 'sad' | 'angry' | 'disgusted' | 'fearful'

export interface EmotionStimulus {
  id: number
  order: number
  title: string
  type: 'video' | 'image' | 'url' | 'text' | 'figma'
  url: string
  payload: string
  exposure_ms: number | null
}

/** Sentinel stimulus id for the merged "all stimuli" timeline (sent as ?stimulus_id=all). */
export const ALL_STIMULI_ID = -1

/** One clip's slot within the merged timeline: where it starts and how long it runs. */
export interface EmotionSegment {
  stimulus_id: number
  order: number
  title: string
  type: 'video' | 'image' | 'url' | 'text' | 'figma'
  url: string
  payload: string
  /** Offset (ms) of this clip's start within the concatenated timeline. */
  start_ms: number
  duration_ms: number
  /** Offset (ms) of this clip's start within the drilled-in respondent's
      continuous face recording — drives the face PiP sync in merged mode. */
  window_start_ms: number
  /** How this segment's length was determined (estimated = inferred from frames). */
  timeline_source?: 'exposure' | 'marker' | 'estimated'
}

export interface EmotionFilterFacet {
  id: number
  text: string
  section_name: string
  is_meta: boolean
  choices: Array<{ value: number; text: string }>
}

export interface EmotionRespondent {
  id: number
  label: string
  status: string
  has_data: boolean
  /** ready → curve available; processing → upload still analysing on the
   *  server; failed → the analysis job errored/stalled (offer Retry);
   *  none → no analysable data (no recording, or succeeded with no faces). */
  analysis_status: 'ready' | 'processing' | 'blocked' | 'failed' | 'none'
  /** Recording to re-dispatch when analysis_status === 'failed'; else null. */
  recording_id?: number | null
  demographics: Record<string, string>
  /** Face still captured at record time; the respondent's avatar. Null if none. */
  thumbnail_url?: string | null
  /** When the reaction was captured (ISO); drives the "16 min ago" label. */
  captured_at?: string | null
}

/** A questionnaire question shown in the results panel (choice or free-text). */
export interface AnswerQuestion {
  id: number
  text: string
  section_name: string
  is_meta: boolean
  type: 'TEXT' | 'SINGLE_CHOICE' | 'MULTIPLE_CHOICE'
  choices: Array<{ value: number; text: string }>
}

/** Answer tallies for one question across the filtered respondent set. */
export interface AnswerDistribution {
  /** choice value (as string) → how many respondents picked it. */
  counts: Record<string, number>
  /** respondents who answered the question at all. */
  responses: number
  /** raw free-text answers (free-text questions only). */
  text_answers: string[]
}

/** One respondent's own answer to a question. */
export interface RespondentAnswer {
  choices: number[]
  text: string
}

/** Per-emotion curve sampled onto the common grid. ``null`` marks a grid point
 *  with no underlying frame (leading/trailing gap) — the timeline breaks the
 *  line there rather than drawing a fabricated flat segment. */
export type EmotionSeries = Record<EmotionKey, (number | null)[]>

/** Gaze x/y track (normalised screen coords 0..1) on its own dense time axis
 *  `t_ms` (50 ms buckets, not the emotion `grid_ms` — too coarse for a moving
 *  dot). `null` marks a bucket with no captured gaze. */
export interface GazeTrack {
  t_ms: number[]
  x: (number | null)[]
  y: (number | null)[]
  /** Group heat map only: whose point each one is (participant id), so the
   *  panel can find every respondent's fixations separately. */
  r?: number[]
}

export interface EmotionAnalysisResponse {
  stimuli: EmotionStimulus[]
  filters: EmotionFilterFacet[]
  emotions: EmotionKey[]
  selected_stimulus_id: number | null
  duration_ms: number
  grid_ms: number[]
  aggregate: EmotionSeries
  sample_counts: number[]
  respondent_count: number
  total_respondent_count: number
  respondents: EmotionRespondent[]
  individual: {
    respondent_id: number
    series: EmotionSeries
    /** Gaze position (normalised screen coords 0..1) on its own `t_ms` axis,
     *  with `null` where no gaze was captured. Drives the gaze dot
     *  drawn over the stimulus player (eye-tracking studies only). */
    gaze: GazeTrack | null
    /** Protected URL of the respondent's face recording (fetch with auth). */
    recording_url: string | null
    /** Offset (ms) of the selected stimulus within that recording. */
    window_start_ms: number
  } | null
  /** Questionnaire questions (drives the answer-distribution plots). */
  questions: AnswerQuestion[]
  /** question id (as string) → answer tallies across the filtered set. */
  answer_distribution: Record<string, AnswerDistribution>
  /** When a respondent is drilled into: their own answers by question id. */
  respondent_answers: Record<string, RespondentAnswer> | null
  /** Merged "all stimuli" mode: the clips that make up the concatenated timeline. */
  segments: EmotionSegment[] | null
  /** True when this response is the stitched all-stimuli reel. */
  is_merged: boolean
  /** Eye-tracking studies: every respondent's gaze pooled for the group heat
   *  map (only real points, `t_ms` on the panel clock). `null` without eye
   *  tracking, on a Figma prototype, or on FaceWise Basic. */
  gaze_heat?: GazeTrack | null
  /** How the timeline length was determined. */
  timeline_source?: 'exposure' | 'marker' | 'estimated'
  /** True when the axis length is inferred from observed frames (no known
   *  exposure and no client marker) — i.e. approximate, possibly clipped. */
  timeline_estimated?: boolean
  /** How many respondents this edition needs before it will draw a curve at
   *  all. `null` on a Pro study, which has no floor and shows partial data. */
  min_respondents: number | null
  /** True when the study sits under that floor. The curves, the respondent
   *  list and the answer tallies above are then deliberately empty — an
   *  average over two people lies — while `respondent_count` stays truthful,
   *  so the screen can show what has come in instead of a result. Never true
   *  for Pro. */
  below_min_respondents: boolean
}

/** Active answer filters: question id → set of selected choice values. */
export type AnswerFilters = Record<number, number[]>

export interface EmotionAnalysisQuery {
  stimulusId?: number | null
  filters?: AnswerFilters
  respondentId?: number | null
  grid?: number
}

/** Whole-study emotion summary for the studies-list columns (one per project). */
export interface ProjectEmotionSummary {
  has_data: boolean
  /** Share of non-neutral emotion (1 − neutral), 0..1. */
  reactivity: number
  /** Strongest non-neutral emotion across the study, or null when no data. */
  dominant: EmotionKey | null
  dominant_share: number
  respondent_count: number
  distribution: Record<EmotionKey, number>
}

class EmotionAnalysisService {
  async get(projectId: number, query: EmotionAnalysisQuery = {}): Promise<EmotionAnalysisResponse> {
    const params = new URLSearchParams()
    if (query.stimulusId === ALL_STIMULI_ID) params.set('stimulus_id', 'all')
    else if (query.stimulusId != null) params.set('stimulus_id', String(query.stimulusId))
    if (query.respondentId != null) params.set('respondent_id', String(query.respondentId))
    if (query.grid != null) params.set('grid', String(query.grid))
    for (const [qid, values] of Object.entries(query.filters ?? {})) {
      for (const v of values) params.append('answer', `${qid}:${v}`)
    }
    const qs = params.toString()
    const url = `${API_CONFIG.ENDPOINTS.PROJECTS.EMOTION_ANALYSIS(projectId)}${qs ? `?${qs}` : ''}`
    const response = await baseService.get<EmotionAnalysisResponse>(url)
    return response.data
  }

  /** Per-project emotion summaries keyed by project id (for the studies list). */
  async summaries(): Promise<Record<string, ProjectEmotionSummary>> {
    const response = await baseService.get<Record<string, ProjectEmotionSummary>>(
      API_CONFIG.ENDPOINTS.PROJECTS.EMOTION_SUMMARIES,
    )
    return response.data
  }
}

export const emotionAnalysisService = new EmotionAnalysisService()
